import { useState, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { ManufacturerModel } from '@models/manufacturer-model';
import {
    useAddManufacturerModelMutation,
    useEditModelOptionByIdMutation,
    useGetManufacturerModelsByIdQuery,
} from '@features/tables/api/systemTables.api';
import { useErrorBoundary } from 'react-error-boundary';
import { handleErrorWithBoundary } from '@shared/lib/errors';

interface UseEditableManufacturerModelOptionsParams {
    manufacturerId?: number | null;
    enabled: boolean;
}

interface UseEditableManufacturerModelOptionsResult {
    items: ReactNode[];
    renderValue: (value: unknown) => ReactNode;
}

/**
 * Same approach as useEditableSystemTableOptions (see that file for why click
 * handlers must live on an inner child element, never on the MenuItem itself, and
 * why a custom `renderValue` is required) - just backed by the manufacturer-models
 * endpoints instead of the system-tables ones, since model options are scoped to
 * one manufacturer rather than being a flat system table.
 */
export function useEditableManufacturerModelOptions({ manufacturerId, enabled }: UseEditableManufacturerModelOptionsParams): UseEditableManufacturerModelOptionsResult {

    const { showBoundary } = useErrorBoundary();

    const { data: options } = useGetManufacturerModelsByIdQuery(manufacturerId ?? 0, { skip: !enabled || !manufacturerId });
    const [editModel] = useEditModelOptionByIdMutation();
    const [addModel] = useAddManufacturerModelMutation();

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState<string>('');
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [newName, setNewName] = useState<string>('');
    const [busy, setBusy] = useState<boolean>(false);


    function startEdit(event: MouseEvent, option: ManufacturerModel): void {
        event.stopPropagation();
        setEditingId(option.id);
        setEditingName(option.name ?? '');
    }


    function cancelEdit(): void {
        setEditingId(null);
        setEditingName('');
    }


    async function commitEdit(option: ManufacturerModel): Promise<void> {

        const trimmedName = editingName.trim();

        if (busy || !trimmedName || trimmedName === (option.name ?? '')) {
            cancelEdit();
            return;
        }

        setBusy(true);

        try {
            await editModel({ ...option, name: trimmedName }).unwrap();
        }

        catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }

        finally {
            setBusy(false);
            cancelEdit();
        }
    }


    function cancelAdd(): void {
        setIsAdding(false);
        setNewName('');
    }


    async function commitAdd(manufacturerId: number): Promise<void> {

        const trimmedName = newName.trim();

        if (busy || !trimmedName) {
            cancelAdd();
            return;
        }

        setBusy(true);

        try {
            const nextIndex = (options?.length ?? 0) + 1;
            const created = await addModel({ manufacturerId, modelIndex: nextIndex }).unwrap();
            await editModel({ ...created, name: trimmedName }).unwrap();
        }

        catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }

        finally {
            setBusy(false);
            cancelAdd();
        }
    }


    function stopKeys(event: KeyboardEvent): void {
        event.stopPropagation();
    }


    function renderValue(value: unknown): ReactNode {

        if (value === '' || value === null || value === undefined) return '';

        const index = (options ?? []).findIndex((option) => option.index === value);

        if (index < 0) return '';

        return `${index + 1}. ${options![index].name ?? ''}`;
    }


    if (!enabled || !manufacturerId) return { items: [], renderValue };


    const items: ReactNode[] = (options ?? []).map((option, index) => (
        <MenuItem
            key={option.id}
            value={option.index}
            sx={{ '&:hover .model-edit-btn': { opacity: 1 } }}
        >
            {
                editingId === option.id ?

                    <Box
                        sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <InputBase
                            autoFocus
                            fullWidth
                            value={editingName}
                            disabled={busy}
                            onChange={(event) => setEditingName(event.target.value)}
                            onBlur={() => commitEdit(option)}
                            onKeyDown={(event) => {
                                stopKeys(event);
                                if (event.key === 'Enter') commitEdit(option);
                                if (event.key === 'Escape') cancelEdit();
                            }}
                        />
                    </Box>

                    :

                    <>
                        <Box sx={{ flex: 1 }}>{index + 1}. {option.name}</Box>
                        <IconButton
                            className="model-edit-btn"
                            size="small"
                            tabIndex={-1}
                            sx={{ opacity: 0, transition: 'opacity .15s' }}
                            onClick={(event) => startEdit(event, option)}
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                            <EditIcon fontSize="inherit" />
                        </IconButton>
                    </>
            }
        </MenuItem>
    ));

    items.push(
        <MenuItem
            key="__add_manufacturer_model__"
            disableRipple
            sx={{ borderTop: 1, borderColor: 'divider', padding: 0 }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', width: '100%', padding: '6px 16px' }}
                onClick={(event) => {
                    event.stopPropagation();
                    if (!isAdding) setIsAdding(true);
                }}
                onMouseDown={(event) => event.stopPropagation()}
            >
                {
                    isAdding ?

                        <InputBase
                            autoFocus
                            fullWidth
                            placeholder="שם חדש"
                            value={newName}
                            disabled={busy}
                            onChange={(event) => setNewName(event.target.value)}
                            onBlur={() => commitAdd(manufacturerId)}
                            onKeyDown={(event) => {
                                stopKeys(event);
                                if (event.key === 'Enter') commitAdd(manufacturerId);
                                if (event.key === 'Escape') cancelAdd();
                            }}
                        />

                        :

                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                            <AddIcon fontSize="small" sx={{ marginInlineEnd: 1 }} />
                            הוספת דגם
                        </Box>
                }
            </Box>
        </MenuItem>
    );

    return { items, renderValue };
}
